import { FormControl, Grid, MenuItem, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validateDate } from "../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import {
  SelectLight,
  TextFieldLight,
} from "../../../../common/components/styled/styled";
import isEmpty from "../../../../common/helpers/isEmpty";
import { VerticalAlignGrid } from "../../../invoices/styled";
import { CustomerContract } from "../../customerAPI";
import { updateContractPricing } from "../../customerContractsSlice";
import { formatDateForBE, formatDateStringForUI } from "../helpers/commonUtils";
import {
  buildSpecialPricingTableDataArray,
  buildTesPricingTableDataArray,
  buildUpdatedPricingObjectForApi,
  validatePricingGeneralSettings,
} from "../helpers/pricingUtils";
import { PricingFormDivider } from "../styles";
import PricingEditModal from "./form/common/PricingEditModal";
import PricingTablesSection from "./form/common/PricingTablesSection";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import { startOfDay } from "date-fns";

interface Props {
  contractData: CustomerContract;
  pricingData: any;
  existingPricingValidFromDates: string[];
  eezyCompanyName: string;
  closeModal: () => void;
}

const NewPricingFromExistingDataForm = ({
  contractData,
  pricingData,
  existingPricingValidFromDates,
  eezyCompanyName,
  closeModal,
}: Props) => {
  const { t } = useTranslation("customers");
  const dispatch = useAppDispatch();
  const { tes } = useAppSelector((state) => state.localized);
  const { status } = useAppSelector((state) => state.customerContracts);
  const isLoading = status === "pending";
  const [selectedPricing, setSelectedPricing] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{
    show: boolean;
    selectedItemId?: string;
  }>({
    show: false,
    selectedItemId: "",
  });
  const isGeneralPricingModal = modalState.selectedItemId === "general";

  const defaultValues = {
    pricingValidFromDate: null,
    pricingGeneralSettings: undefined,
    pricingTesList: [],
  };

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    setError,
    formState: { isDirty, errors, isSubmitting },
  } = methods;

  const watchedValidFromDate = watch("pricingValidFromDate");
  const watchedPricingGeneralSettings = watch("pricingGeneralSettings");
  const watchedPricingTesList = watch("pricingTesList");

  const handlePricingSelection = (pricingDate: string) => {
    const foundPricing = pricingData.find(
      (pricing: any) => pricing.pricingValidFromDate === pricingDate,
    );
    if (foundPricing) {
      setValue("pricingTesList", foundPricing.pricingTesList);
      setValue("pricingGeneralSettings", foundPricing.pricingGeneralSettings);
    }
    setError("pricingValidFromDate", {
      message: t("common:requiredField") || "",
    });
    setSelectedPricing(pricingDate);
  };

  const onSubmit = (formData: any) => {
    const updatedPricing = buildUpdatedPricingObjectForApi({
      contractId: contractData.id,
      contractVersion: contractData.version,
      validFrom: formatDateForBE(formData.pricingValidFromDate),
      pricingGeneralSettings: formData.pricingGeneralSettings,
      pricingTesList: formData.pricingTesList,
    });

    dispatch(updateContractPricing(updatedPricing)).then((action) => {
      if (isFulfilled(action)) closeModal();
    });
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    reset(defaultValues);
  };

  const handleClose = () => {
    onCancel();
    closeModal();
  };

  const onOpenModal = (selectedItemId?: string) =>
    setModalState({ show: true, selectedItemId });

  const onCloseModal = () => setModalState({ ...modalState, show: false });

  const onModalExited = () => {
    setModalState({ show: false, selectedItemId: "" });
  };

  const tesPricingDataArray = buildTesPricingTableDataArray({
    pricingTesList: watchedPricingTesList,
    tesLocalizationIndex: tes,
  });

  const specialPricingDataArray = buildSpecialPricingTableDataArray({
    pricingGeneralSettings: watchedPricingGeneralSettings,
    label: t("contract.generalPricing"),
  });

  const getPricingPerTesData = () => {
    const foundData = watchedPricingTesList.find(
      (tesItem: any) => tesItem.tesId === modalState.selectedItemId,
    );
    return {
      pricingValidFromDate: formatDateForBE(watchedValidFromDate),
      pricingTesItem: foundData || {},
    };
  };

  const getPricingGeneralSettingsData = () => {
    return {
      pricingValidFromDate: formatDateForBE(watchedValidFromDate),
      pricingGeneralSettings: watchedPricingGeneralSettings,
    };
  };

  const areValuesValid = validatePricingGeneralSettings(
    watchedPricingGeneralSettings,
  );

  const saveDisabled =
    !isDirty ||
    !areValuesValid ||
    !isEmpty(errors) ||
    isSubmitting ||
    isLoading;

  return (
    <DialogContentWrapper
      title={t("contract.pricing")}
      margin
      onCancel={handleClose}
      onSave={onSave}
      saveDisabled={saveDisabled}
      childContainerSx={{ overflowY: "auto", overflowX: "hidden" }}
      customSaveButtonLabel={t("contract.savePricing")}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <OverlaySpinner isLoading={isLoading} />
          <VerticalAlignGrid container spacing={1}>
            <Grid
              container
              item
              xs={6}
              justifyContent="flex-end"
              paddingRight="0.5rem"
            >
              <Typography variant="secondary">
                {t("contract.selectPricingToBeCopied")}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth size="small">
                <SelectLight
                  labelId="label"
                  value={selectedPricing || ""}
                  onChange={(event) =>
                    handlePricingSelection(event.target.value as string)
                  }
                  displayEmpty
                  renderValue={(value) =>
                    value
                      ? formatDateStringForUI(value as string)
                      : t("contract.selectPricingDate")
                  }
                >
                  {existingPricingValidFromDates.map((pricingDate) => (
                    <MenuItem key={pricingDate} value={pricingDate} dense>
                      {formatDateStringForUI(pricingDate)}
                    </MenuItem>
                  ))}
                </SelectLight>
              </FormControl>
            </Grid>
          </VerticalAlignGrid>
          {selectedPricing ? (
            <>
              <VerticalAlignGrid container spacing={1}>
                <Grid
                  container
                  item
                  xs={6}
                  justifyContent="flex-end"
                  paddingRight="0.5rem"
                >
                  <Typography variant="secondary">
                    {t("contract.newPricingValidFrom")}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name="pricingValidFromDate"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("common:requiredField"),
                      },
                      validate: {
                        validDate: (date) =>
                          (date && validateDate(date, "date")) ||
                          t("common:invalidDate") ||
                          "",
                        currentOrFutureDate: (date: any) => {
                          if (!validateDate(date, "date")) return;
                          return (
                            startOfDay(date).getTime() >=
                              startOfDay(new Date()).getTime() ||
                            t("common:currentOrFutureDate") ||
                            ""
                          );
                        },
                        uniqueDate: (date: any) => {
                          if (!validateDate(date, "date")) return;
                          // Allow editing of existing pricing
                          if (
                            formatDateForBE(date) ===
                            pricingData.pricingValidFromDate
                          )
                            return true;
                          // Check if date is already used in another pricing
                          return (
                            !existingPricingValidFromDates.includes(
                              formatDateForBE(date),
                            ) ||
                            t("customers:contract.pricingAlreadyExists") ||
                            ""
                          );
                        },
                      },
                    }}
                    render={({ field }) => (
                      <DesktopDatePicker
                        {...field}
                        value={field.value}
                        format="dd.MM.yyyy"
                        onChange={(newDate: Date | null) =>
                          field.onChange(newDate)
                        }
                        slots={{ textField: TextFieldLight }}
                        slotProps={{
                          textField: {
                            sx: { width: "135px" },
                            error: !!errors.pricingValidFromDate,
                            helperText: errors.pricingValidFromDate?.message,
                            FormHelperTextProps: {
                              sx: {
                                position: "absolute",
                                top: "2.3rem",
                                width: "250px",
                              },
                            },
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </VerticalAlignGrid>
              <PricingTablesSection
                generalPricingDataArray={specialPricingDataArray}
                tesPricingDataArray={tesPricingDataArray}
                onOpenModal={onOpenModal}
              />
              <PricingEditModal
                isOpen={modalState.show}
                onClose={onCloseModal}
                openedFromPricingContent
                isGeneralPricingModal={isGeneralPricingModal}
                contractData={contractData}
                eezyCompanyName={eezyCompanyName}
                pricingPerTesData={getPricingPerTesData()}
                pricingGeneralSettingsData={getPricingGeneralSettingsData()}
                TransitionProps={{ onExited: onModalExited }}
              />
            </>
          ) : (
            <PricingFormDivider />
          )}
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default NewPricingFromExistingDataForm;
